import { useState } from "react";
import {
  ChartColumnBig, ExternalLink, FolderSearch, GalleryHorizontalEnd, Gauge, Images, MousePointerClick,
  PersonStanding, Route, Search, ShieldAlert, TableProperties, Utensils,
} from "lucide-react";
import ProductLayout from "./sai/ProductLayout";
import Showcase from "./sai/Showcase";
import { VIDEOS } from "./sai/DemoVideo";
import { IDS as STEP_IDS } from "./sai/ids";
import { IDS } from "./vizAppIds";
import "./VizApp.css";

// Viz App, ported from sai's VizAppScreen (brochure-demo @ 2a7c405) into our
// shell: ProductLayout renders our <Screen> via SaiProductFrame; IDs are
// data-trapezium-* via toTrapezium; "Next product" follows our STEPS.
//
// His notes: the showcase video on top, then one screen, no tabs (the user,
// 2026-10-10): the Viz App in a phone
// for a demo patient (John Demo) on the left; on the right, the five parts of
// the app as rows. Tapping a row shows that screen from the live app (same-site
// screenshots, described in words), so the rows drive the phone and the
// assistant can read it (guide: do not depend on a cross-origin iframe). "Try
// it live" swaps the live app into the same phone to scroll and tap; "Open full
// screen" opens it on its own, labelled as an external site. The rows only say
// what each part of the app does today. Above the rows, the app's own two
// views, Overview and Smart View (the user, 2026-10-10), each with its parts.
// Screens captured from LIVE at 375 x 700 (2026-10).

const SCREEN = "viz-app"; // his screen key (our product.viz-app)
const LIVE = (path) => `https://feature-report-engine-vizapp.d2psssbi1dbgfm.amplifyapp.com/${path}?id=5ace2d633b75591a8fbce202ebf2e7ab4d2eecfbe6a0760a6d5cc394a606ac23`;

// The Overview's five parts: the row's icon and words, and what its screen shows (alt text).
const OVERVIEW = [
  { key: "score", icon: Gauge, name: "Score", line: "A wellness score out of 100, from Poor to Optimal.",
    shot: "Viz App overview for the demo patient: a wellness score of 72 out of 100 (Good), on a scale from Poor to Optimal." },
  { key: "body-map", icon: PersonStanding, name: "Body map", line: "Tap an organ to open its profile, in colour.",
    shot: "Viz App body map: each test profile placed on the body; the ones needing attention are ringed in red." },
  { key: "diet", icon: Utensils, name: "Diet", line: "What to eat more of, and what to limit.",
    shot: "Viz App diet recommendations: a core guideline (30% protein, 40% fat, 30% carbs), Eat more and Limit, and food groups to explore." },
  { key: "risk", icon: ShieldAlert, name: "Risk", line: "Thyroid, lipid and diabetes risk at a tap.",
    shot: "Viz App risk calculator: thyroid and diabetes at low risk, lipid at mild attention; tap a bubble for the score." },
  { key: "next-tests", icon: Route, name: "Next tests", line: "A diagnostic roadmap: the next tests, in order.",
    shot: "Viz App next steps: no symptoms flagged, a three-step diagnostic roadmap, and the medical disclaimer." },
];
// Smart View: every profile and every value.
const SMART = [
  { key: "smart-view", icon: GalleryHorizontalEnd, name: "Detailed view", line: "A card for each test profile, to swipe through.",
    shot: "Viz App Smart View: Detailed View, a card for each test profile (Liver Profile, Diabetes Monitoring and more) to swipe through." },
  { key: "all-profiles", icon: FolderSearch, name: "All profiles", line: "Normal and abnormal counts, with search.",
    shot: "Viz App All Profiles: a search bar, an Abnormal Only switch, and each profile with its normal and abnormal counts." },
  { key: "profile-detail", icon: TableProperties, name: "Profile details", line: "Each value beside its unit and range.",
    shot: "Viz App Lipid Profile opened: 4 normal, 5 abnormal; each test with its result, unit and range, abnormal ones marked in red." },
];
const VIEWS = [
  { key: "overview", name: "Overview", icon: Search, path: "overview", parts: OVERVIEW },
  { key: "smart", name: "Smart View", icon: ChartColumnBig, path: "smartview", parts: SMART },
];
const PARTS = [...OVERVIEW, ...SMART]; // all eight screens; state step-<part key>, e.g. step-score

const STEPS = [
  { key: "app", nav: "Viz App", head: "Tap. Explore. Understand.",
    lede: "The Viz App for a demo patient, in its two views. Tap a part to see it on the phone.", points: [] },
];

export default function VizApp() {
  const [view, setView] = useState(0);
  const [active, setActive] = useState(0); // the part, within the view
  const [live, setLive] = useState(false);
  const { parts, path } = VIEWS[view];
  const shown = parts[active].key;
  const show = (i) => { setActive(i); setLive(false); };
  // A view opens on its first part; live, the app switches view too.
  const pickView = (v) => { setView(v); setActive(0); };

  const visual = (
    <div className="va-live" data-trapezium-view={live ? "live" : `screen-${shown}`}>
      <div className="va-device">
        <div className="va-phone">
          <div className="va-screen">
            {live ? (
              <iframe src={LIVE(path)} title="Viz App for a demo patient (live, external site)" />
            ) : PARTS.map((p) => (
              // All eight stay loaded; the part's screen fades in over the last.
              <img key={p.key} className="va-shot" src={`/demo/viz-app/${p.key}.jpg`} width="750" height="1400"
                alt={p.shot} aria-hidden={p.key === shown ? undefined : "true"} data-on={p.key === shown ? "true" : undefined} />
            ))}
          </div>
        </div>
        {/* Just outside the phone, under the frame's bottom-right corner: never over the screen. */}
        <a className="va-open" href={LIVE(path)} target="_blank" rel="noopener noreferrer" data-trapezium-id={IDS.openLive}>
          <span className="va-open-btn">
            <ExternalLink size={16} strokeWidth={2.4} aria-hidden="true" />
            Open full screen
          </span>
          <span className="va-ext">Opens external site</span>
        </a>
      </div>
      <div className="va-actions">
        <button type="button" className="va-try" aria-pressed={live} onClick={() => setLive(!live)} data-trapezium-id={IDS.showLive}>
          {live ? <Images size={15} aria-hidden="true" /> : <MousePointerClick size={15} aria-hidden="true" />}
          {live ? "Back to the screens" : "Try it live in the phone"}
        </button>
      </div>
    </div>
  );

  const views = (
    <div className="va-views" role="group" aria-label="Viz App views">
      {VIEWS.map((v, i) => (
        <button key={v.key} type="button" aria-pressed={i === view} onClick={() => pickView(i)} data-trapezium-id={IDS.view(v.key)}>
          <v.icon size={16} strokeWidth={2.2} aria-hidden="true" />
          {v.name}
        </button>
      ))}
    </div>
  );

  const rows = (
    <ol className="prod-rows" aria-label={`Parts of the Viz App: ${VIEWS[view].name}`} key={view}>
      {parts.map((p, i) => (
        <li key={p.key} style={{ "--i": i }}>
          <button type="button" aria-pressed={!live && i === active} onClick={() => show(i)} data-trapezium-id={STEP_IDS.step(SCREEN, p.key)}>
            <span className="prod-row-n va-row-n" aria-hidden="true"><p.icon size={15} strokeWidth={2.2} />{String(i + 1).padStart(2, "0")}</span>
            <span className="prod-row-t"><b>{p.name}</b><span>{p.line}</span></span>
          </button>
        </li>
      ))}
    </ol>
  );

  return (
    <ProductLayout
      screenKey={SCREEN}
      steps={STEPS}
      active={0}
      onStep={() => {}}
      state={`step-${shown}`}
      visual={visual}
      controls={<>{views}{rows}</>}
      above={(
        <Showcase
          video={{ ...VIDEOS.vizShowcase.wide, title: VIDEOS.vizShowcase.title }}
          title="See the Viz App in action"
          points={["Your health, at a glance", "Tap. Explore. Understand.", "Every test explained", "Diet, risk and next steps", "Private by design"]}
          soundId={IDS.showcaseSound}
          playId={IDS.showcasePlay}
        />
      )}
      note={live ? "The live Viz App, for a demo patient. Scroll and tap inside the phone." : "Screens from the live Viz App, for a demo patient. Try it live to scroll and tap."}
    />
  );
}
